<script setup>
import { onBeforeUnmount, onMounted, ref, computed } from 'vue'
import { useGlobalStore } from '@stores/global'
import * as THREE from 'three'

import { INFINITE_GALLERY_VERTEX_SHADER, INFINITE_GALLERY_FRAGMENT_SHADER } from '../../utils/shaders'

// ============================================================================
// CONFIGURATION
// ============================================================================
/**
 * Gallery configuration options
 * @type {Object}
 */
// Detect Safari (not Chrome or Firefox)
const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(navigator.userAgent)

// Safari: Polyfill requestAnimationFrame to avoid throttling (point 7)
if (isSafari && typeof window !== 'undefined') {
	let lastTime = 0
	window.requestAnimationFrame =
		window.requestAnimationFrame ||
		function (callback) {
			const currTime = Date.now()
			const timeToCall = Math.max(0, 16 - (currTime - lastTime))
			const id = window.setTimeout(function () {
				callback(currTime + timeToCall)
			}, timeToCall)
			lastTime = currTime + timeToCall
			return id
		}
	window.cancelAnimationFrame =
		window.cancelAnimationFrame ||
		function (id) {
			clearTimeout(id)
		}
}

const config = {
	/** @type {number} Gap between cells (0-1) */
	cellGap: 0.06,
	/** @type {number} Maximum zoom level when dragging */
	zoomLevel: 1.25,
	/** @type {number} Animation smoothing factor (0-1) */
	lerpFactor: isSafari ? 0.18 : 0.1,
	/** @type {string} RGBA border color */
	borderColor: 'rgba(255, 255, 255, 0.15)',
	/** @type {string} RGBA background color */
	backgroundColor: 'rgba(0, 0, 0, 1)',
	/** @type {string} RGBA text color */
	textColor: 'rgba(128, 128, 128, 1)',
	/** @type {string} RGBA hover overlay color */
	hoverColor: 'rgba(255, 255, 255, 0)',
	/** @type {number} Vignette intensity (0-1) */
	vignetteIntensity: 0.7,
	/** @type {number} Vignette start radius */
	vignetteRadius: 1.3,
	/** @type {number} Vignette fade softness */
	vignetteSoftness: 0.5,
	/** @type {number} Number of radial blur samples */
	radialBlurSamples: isSafari ? 4 : 8,
	/** @type {number} Radial blur intensity on drag */
	radialBlurIntensity: isSafari ? 0.75 : 1.3,
	/** @type {number} Atlas tile size in pixels */
	atlasTextureSize: isSafari ? 512 : 1024,
}

// ============================================================================
// STATE
// ============================================================================
const NO_HOVER_VALUE = -999
const INTRO_DURATION = 2000
const INTRO_DELAY = 0

function easeOutQuint(x) {
	return 1 - Math.pow(1 - x, 5)
}

const globalStore = useGlobalStore()
const relics = computed(() => globalStore.relics)

const canvasRef = ref(null)

// Three.js instances
let scene = null
let camera = null
let renderer = null
let plane = null
let textureLoader = null

// Atlases
let imageAtlas = null
let textAtlas = null

// Lifecycle flags
let isDestroyed = false
let isDragging = false
let isClick = false
let isTouchActive = false
let clickStartTime = 0
let introProgress = 0
let introComplete = false
let introStartTime = null

// Mouse state
const touchStartPosition = { x: -1, y: -1 }
const mousePosition = { x: -1, y: -1 }
const previousMouse = { x: 0, y: 0 }

// Scroll offset
const offset = new THREE.Vector2()
const targetOffset = new THREE.Vector2()

// Zoom & distortion
let zoomLevel = 1
let currentDistortion = 0
let targetDistortion = 0

// Hover state
let currentHoverIntensity = 0
let targetHoverIntensity = 0
let prevHoverIntensity = 0
const hoveredCell = new THREE.Vector2(NO_HOVER_VALUE, NO_HOVER_VALUE)
const prevHoveredCell = new THREE.Vector2(NO_HOVER_VALUE, NO_HOVER_VALUE)

// Media storage
const videoTextures = []
const imageTextures = []
const textTextures = []
const textCanvases = []
const imageAspectRatios = []
const videoElements = []
const isVideoTexture = []
const atlasVideoPatched = []

// ============================================================================
// LAYOUT CONFIGURATION
// ============================================================================

/** @constant {number} Number of columns in masonry grid */
let NUM_COLUMNS = 3

/** @constant {number} Width of each column in world units */
const COLUMN_WIDTH = 1.0

/** @constant {number[]} Y offset for each column (stagger effect) */
const COLUMN_OFFSETS = [0, 0.3, 0.15]

/** @type {Array<{cells: ColumnCell[], totalHeight: number}>} */
const columnData = []

let layoutTexture = null

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Proper modulo that handles negative numbers correctly
 * @param {number} n - Dividend
 * @param {number} m - Divisor
 * @returns {number} Always positive result
 */
const mod = (n, m) => ((n % m) + m) % m

/**
 * Linear interpolation between two values
 * @param {number} current - Current value
 * @param {number} target - Target value
 * @param {number} factor - Interpolation factor (0-1)
 * @returns {number} Interpolated value
 */
const lerp = (current, target, factor) => current + (target - current) * factor

/**
 * Parse RGBA color string to normalized array
 * @param {string} rgba - RGBA color string like 'rgba(255, 128, 0, 0.5)'
 * @returns {number[]} Normalized [r, g, b, a] array (0-1)
 */
const rgbaToArray = (rgba) => {
	const match = rgba.match(/rgba?\(([^)]+)\)/)
	if (!match) return [1, 1, 1, 1]
	return match[1].split(',').map((v, i) => (i < 3 ? parseFloat(v.trim()) / 255 : parseFloat(v.trim() || 1)))
}

/**
 * Check if URL points to a video file
 * @param {string} url - Media URL
 * @returns {boolean} True if URL is a video
 */
const isVideoUrl = (item) => (item.video ? true : false)

/**
 * Apply common texture configuration
 * @param {THREE.Texture} texture - Texture to configure
 */
const configureTexture = (texture) => {
	texture.wrapS = texture.wrapT = THREE.ClampToEdgeWrapping
	texture.minFilter = texture.magFilter = THREE.LinearFilter
	texture.generateMipmaps = false
}

// ============================================================================
// LAYOUT GENERATION
// ============================================================================
/**
 * Update grid size based on viewport size
 */
const updateGridDimensions = (width, height) => {
	const aspect = width / height
	const frustumWidth = 2.0 * aspect * config.zoomLevel

	NUM_COLUMNS = Math.max(3, Math.ceil(frustumWidth / COLUMN_WIDTH) + 2)
}

/**
 * Generate masonry column layout from loaded image aspect ratios
 */
const generateColumnLayout = () => {
	columnData.length = 0
	const numImages = imageAspectRatios.length
	if (numImages === 0) return

	for (let col = 0; col < NUM_COLUMNS; col++) {
		columnData.push({ cells: [], totalHeight: 0 })
	}

	for (let i = 0; i < numImages; i++) {
		const col = i % NUM_COLUMNS
		const cellHeight = imageAspectRatios[i] * COLUMN_WIDTH

		columnData[col].cells.push({
			imageIndex: i,
			y: columnData[col].totalHeight,
			height: cellHeight,
		})

		columnData[col].totalHeight += cellHeight
	}
}

/**
 * Create GPU texture containing layout data for shader consumption
 * @returns {THREE.DataTexture} Layout data texture
 */
const createLayoutTexture = () => {
	const texRows = NUM_COLUMNS + 1
	const data = new Float32Array(64 * texRows * 4)

	for (let col = 0; col < NUM_COLUMNS; col++) {
		const idx = col * 4
		data[idx + 0] = columnData[col]?.totalHeight || 1.0
		data[idx + 1] = COLUMN_OFFSETS[col % COLUMN_OFFSETS.length] || 0
		data[idx + 2] = columnData[col]?.cells.length || 0
		data[idx + 3] = 0
	}

	for (let col = 0; col < NUM_COLUMNS; col++) {
		const cells = columnData[col]?.cells || []
		for (let i = 0; i < cells.length && i < 64; i++) {
			const rowOffset = (1 + col) * 64 * 4
			const idx = rowOffset + i * 4
			data[idx + 0] = cells[i].y
			data[idx + 1] = cells[i].height
			data[idx + 2] = cells[i].imageIndex
			data[idx + 3] = isVideoTexture[cells[i].imageIndex] ? 1.0 : 0.0
		}
	}

	const texture = new THREE.DataTexture(data, 64, texRows, THREE.RGBAFormat, THREE.FloatType)
	texture.needsUpdate = true
	return texture
}

// ============================================================================
// VIDEO MANAGEMENT
// ============================================================================
/**
 * Create a muted, looped video element for texture use
 * @param {string} url - Video URL
 * @returns {HTMLVideoElement} Configured video element
 */
const createVideoElement = (url) => {
	const video = document.createElement('video')
	video.src = url
	video.muted = true
	video.loop = true
	video.crossOrigin = 'anonymous'
	video.playsInline = true
	video.setAttribute('webkit-playsinline', 'true')
	video.style.display = 'none'

	document.body.appendChild(video)

	video.load()
	video.pause()

	return video
}

// ============================================================================
// TEXTURE CREATION
// ============================================================================
/**
 * Create a text overlay texture canvas
 * @param {string} text - Text content to render
 * @returns {THREE.CanvasTexture} Text texture
 */
const createTextTexture = (text, client) => {
	const canvasSize = 1024
	const fontSize = 40
	const canvas = document.createElement('canvas')
	canvas.width = canvasSize
	canvas.height = canvasSize
	const context = canvas.getContext('2d', { alpha: true })
	context.clearRect(0, 0, canvas.width, canvas.height)

	const padding = 50
	const badgeGap = 65
	const badgeRadius = 35
	const bottomOffset = 0
	const badgeCenterX = padding + badgeRadius
	const badgeCenterY = canvasSize - padding - badgeRadius - bottomOffset

	if (client) {
		const initials = client

		context.beginPath()
		context.arc(badgeCenterX, badgeCenterY, badgeRadius, 0, Math.PI * 2)
		context.fillStyle = 'rgba(255, 255, 255, 0.9)'
		context.fill()

		context.fillStyle = 'rgba(0, 0, 0, 1)'
		context.font = `bold ${badgeRadius * 0.95}px Arial`
		context.textAlign = 'center'
		context.textBaseline = 'middle'
		context.fillText(initials, badgeCenterX, badgeCenterY + 1)
	}

	if (text) {
		const words = text.split(' ')
		const lines = [words.slice(0, 4).join(' '), words.slice(4, 8).join(' ')].filter(Boolean)
		const lineHeight = fontSize * 1.3
		const textStartX = client ? badgeCenterX + badgeGap : padding
		const startY = canvasSize - padding - bottomOffset

		context.fillStyle = 'rgba(255, 255, 255, .9)'
		context.font = `normal ${fontSize}px Arial`
		context.textBaseline = 'bottom'
		context.textAlign = 'left'

		lines.forEach((line, index) => {
			const y = startY - (lines.length - 0.8 - index) * lineHeight
			context.fillText(line, textStartX, y)
		})
	}

	textCanvases.push(canvas)

	const texture = new THREE.CanvasTexture(canvas)
	configureTexture(texture)

	texture.flipY = false
	texture.colorSpace = THREE.SRGBColorSpace
	texture.format = THREE.RGBAFormat

	return texture
}

/**
 * Create texture atlas combining multiple textures into one
 * @param {THREE.Texture[]} textures - Array of textures
 * @param {boolean} [isText=false] - Whether this is a text atlas
 * @returns {THREE.CanvasTexture} Combined atlas texture
 */
const createTextureAtlas = (textures, isText = false) => {
	const count = textures.length
	if (count === 0) return null

	const atlasSize = Math.ceil(Math.sqrt(count))
	const { atlasTextureSize: tileSize } = config

	const canvas = document.createElement('canvas')
	canvas.width = canvas.height = atlasSize * tileSize

	const context = canvas.getContext('2d', { alpha: true })

	if (isText) {
		context.clearRect(0, 0, canvas.width, canvas.height)
	} else {
		context.fillStyle = 'black'
		context.fillRect(0, 0, canvas.width, canvas.height)
	}

	textures.forEach((texture, index) => {
		const x = (index % atlasSize) * tileSize
		const y = Math.floor(index / atlasSize) * tileSize

		let source = null

		if (isText) {
			source = texture.image
		} else if (isVideoTexture[index]) {
			source = videoElements[index]
			if (!source || source.readyState < 2) {
				console.warn(`Video ${index} non pronto, salto...`)
				return
			}
		} else {
			source = texture.image
		}

		if (source) {
			try {
				context.drawImage(source, x, y, tileSize, tileSize)
			} catch (e) {
				console.error(`Errore nel disegno della texture ${index}:`, e)
			}
		}
	})

	const atlasTexture = new THREE.CanvasTexture(canvas)
	configureTexture(atlasTexture)
	atlasTexture.needsUpdate = true

	return atlasTexture
}

const patchVideoTileInAtlas = (index) => {
	if (atlasVideoPatched[index]) return
	const video = videoElements[index]
	if (!video || video.readyState < 2) return
	if (!imageAtlas?.image) return

	const canvas = imageAtlas.image
	const context = canvas.getContext('2d')
	const atlasGridSize = Math.ceil(Math.sqrt(imageTextures.length))
	const { atlasTextureSize: textureSize } = config

	const tileX = (index % atlasGridSize) * textureSize
	const tileY = Math.floor(index / atlasGridSize) * textureSize

	try {
		context.drawImage(video, tileX, tileY, textureSize, textureSize)
		imageAtlas.needsUpdate = true
		atlasVideoPatched[index] = true
	} catch {}
}

/**
 * Load all media textures (images and videos)
 * @returns {Promise<THREE.Texture[]>} Loaded image textures
 */
const loadTexture = async () => {
	const loadPromises = relics.value.map((item, index) => {
		return new Promise((resolve) => {
			if (isVideoUrl(item)) {
				const video = createVideoElement(item.video)
				videoElements[index] = video
				isVideoTexture[index] = true

				const videoTexture = new THREE.VideoTexture(video)
				configureTexture(videoTexture)
				videoTextures[index] = videoTexture
				imageTextures.push(videoTexture)

				video.addEventListener('loadedmetadata', () => {
					imageAspectRatios[index] = video.videoHeight / video.videoWidth
					resolve({ texture: videoTexture, text: createTextTexture(item.title || '', item.user?.stamp || '') })
				})

				video.addEventListener(
					'canplay',
					() => {
						video.currentTime = 0

						video.addEventListener(
							'seeked',
							() => {
								patchVideoTileInAtlas(index)
								video.pause()
							},
							{ once: true },
						)
					},
					{ once: true },
				)

				video.addEventListener('error', () => {
					console.error(`Errore video: ${item.video}`)
					imageAspectRatios[index] = 1.0
					resolve({ texture: videoTexture, text: createTextTexture(item.title || '', item.user?.stamp || '') })
				})
			} else {
				isVideoTexture[index] = false

				textureLoader.load(
					item.image,
					(texture) => {
						// On Load
						configureTexture(texture)
						imageAspectRatios[index] = texture.image ? texture.image.height / texture.image.width : 1.0
						resolve({ texture: texture, text: createTextTexture(item.title || '', item.user?.stamp || '') })
					},
					undefined, // On Progress
					(err) => {
						// On Error
						console.error(`Errore immagine: ${item.image}`, err)
						imageAspectRatios[index] = 1.0
						const fallbackTexture = new THREE.Texture() // Texture vuota per non rompere i materiali
						resolve({ texture: fallbackTexture, text: createTextTexture(item.title || '', item.user?.stamp || '') })
					},
				)
			}
		})
	})

	const results = await Promise.all(loadPromises)
	results.forEach((res, i) => {
		imageTextures[i] = res.texture
		textTextures[i] = res.text
	})

	return imageTextures
}

/**
 * Create shader uniforms object
 * @param {HTMLElement} container - Canvas container element
 * @returns {Object} Uniforms object for shader material
 */
const createShaderUniforms = (container) => ({
	uOffset: { value: new THREE.Vector2(0, 0) },
	uResolution: { value: new THREE.Vector2(container.offsetWidth, container.offsetHeight) },
	uBorderColor: { value: new THREE.Vector4(...rgbaToArray(config.borderColor)) },
	uHoverColor: { value: new THREE.Vector4(...rgbaToArray(config.hoverColor)) },
	uBackgroundColor: { value: new THREE.Vector4(...rgbaToArray(config.backgroundColor)) },
	uZoom: { value: 1 },
	uColumnWidth: { value: COLUMN_WIDTH },
	uNumColumns: { value: NUM_COLUMNS },
	uTexRows: { value: NUM_COLUMNS + 1.0 },
	uCellGap: { value: config.cellGap },
	uDistortion: { value: 0 },
	uTextureCount: { value: relics.value.length },
	uAtlasSize: { value: Math.ceil(Math.sqrt(relics.value.length)) },
	uImageAtlas: { value: imageAtlas },
	uTextAtlas: { value: textAtlas },
	uLayoutTexture: { value: layoutTexture },
	uHoverIntensity: { value: 0 },
	uHoveredCell: { value: new THREE.Vector2(NO_HOVER_VALUE, NO_HOVER_VALUE) },
	uPrevHoverIntensity: { value: 0 },
	uPrevHoveredCell: { value: new THREE.Vector2(NO_HOVER_VALUE, NO_HOVER_VALUE) },
	// Vignette uniforms
	uVignetteIntensity: { value: config.vignetteIntensity },
	uVignetteRadius: { value: config.vignetteRadius },
	uVignetteSoftness: { value: config.vignetteSoftness },
	// Radial blur uniforms
	uRadialBlurIntensity: { value: config.radialBlurIntensity },
	uRadialBlurSamples: { value: config.radialBlurSamples },
	uActiveVideoTexture: { value: null },
	uActiveVideoIndex: { value: -1.0 },
	uIntroProgress: { value: 0.0 },
})

// ============================================================================
// EVENT HANDLERS
// ============================================================================
/**
 * Update mouse position from event
 * @param {MouseEvent} event
 */
const updateMousePosition = (event) => {
	const rect = renderer.domElement.getBoundingClientRect()
	mousePosition.x = event.clientX - rect.left
	mousePosition.y = event.clientY - rect.top
}

/** Reset mouse position when leaving canvas */
const resetMousePosition = () => {
	mousePosition.x = mousePosition.y = -1
}

/**
 * Start drag interaction
 * @param {number} x - Start X position
 * @param {number} y - Start Y position
 */
const startDrag = (x, y) => {
	isDragging = true
	isClick = true
	clickStartTime = Date.now()

	renderer.domElement.classList.add('dragging')

	previousMouse.x = x
	previousMouse.y = y
}

/** @param {MouseEvent} event */
const onPointerDown = (event) => startDrag(event.clientX, event.clientY)

/** @param {TouchEvent} event */
const onTouchMove = (event) => {
	event.preventDefault()
	handleMove(event.touches[0].clientX, event.touches[0].clientY, true)
}

/**
 * Handle pointer movement during drag
 * @param {number} x - Current X position
 * @param {number} y - Current Y position
 */
const handleMove = (x, y, isTouch = false) => {
	if (!isDragging) return

	const deltaX = x - previousMouse.x
	const deltaY = y - previousMouse.y

	if (Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2) {
		isClick = false
	}

	const dragMultiplier = isTouch ? 0.005 : 0.003

	targetOffset.x -= deltaX * dragMultiplier
	targetOffset.y += deltaY * dragMultiplier
	previousMouse.x = x
	previousMouse.y = y
}

/** @param {MouseEvent} event */
const onPointerMove = (event) => handleMove(event.clientX, event.clientY)

/** @param {TouchEvent} event */
const onTouchStart = (event) => {
	event.preventDefault()
	isTouchActive = true
	const touch = event.touches[0]
	startDrag(touch.clientX, touch.clientY)

	const rect = renderer.domElement.getBoundingClientRect()
	touchStartPosition.x = touch.clientX - rect.left
	touchStartPosition.y = touch.clientY - rect.top
}

/** End drag interaction */
const onPointerUp = (event) => {
	isDragging = false
	renderer.domElement.classList.remove('dragging')

	if (isClick && event.changedTouches) {
		const container = canvasRef.value
		if (container) {
			const { offsetWidth: width, offsetHeight: height } = container
			const worldCoord = mouseToWorldCoords(touchStartPosition.x, touchStartPosition.y, width, height)
			const cell = findCellAtPosition(worldCoord.x, worldCoord.y)

			console.log(cell)

			if (cell) {
				const isSameCell = cell.cellX === hoveredCell.x && cell.cellY === hoveredCell.y

				prevHoveredCell.set(hoveredCell.x, hoveredCell.y)
				prevHoverIntensity = currentHoverIntensity

				if (isSameCell) {
					hoveredCell.set(NO_HOVER_VALUE, NO_HOVER_VALUE)
					targetHoverIntensity = 0
				} else {
					hoveredCell.set(cell.cellX, cell.cellY)
					currentHoverIntensity = 0
					targetHoverIntensity = 1
				}
			} else {
				prevHoveredCell.set(hoveredCell.x, hoveredCell.y)
				prevHoverIntensity = currentHoverIntensity
				hoveredCell.set(NO_HOVER_VALUE, NO_HOVER_VALUE)
				targetHoverIntensity = 0
			}
		}
	}

	isClick = false
}

/** Handle window resize */
const onWindowResize = () => {
	if (!canvasRef.value) return

	const { offsetWidth: width, offsetHeight: height } = canvasRef.value

	camera.updateProjectionMatrix()
	renderer.setSize(width, height)
	renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

	plane?.material.uniforms.uResolution.value.set(width, height)
}

/** Add all event listeners to canvas and window */
const addEventListeners = () => {
	renderer.domElement.addEventListener('mousedown', onPointerDown)
	renderer.domElement.addEventListener('mousemove', onPointerMove)
	renderer.domElement.addEventListener('mouseup', onPointerUp)
	renderer.domElement.addEventListener('mouseleave', onPointerUp)

	// passive: false needed because we call preventDefault() in touch handlers
	renderer.domElement.addEventListener('touchstart', onTouchStart, { passive: false })
	renderer.domElement.addEventListener('touchmove', onTouchMove, { passive: false })
	renderer.domElement.addEventListener('touchend', onPointerUp, { passive: true })

	window.addEventListener('resize', onWindowResize)

	renderer.domElement.addEventListener('mousemove', updateMousePosition)
	renderer.domElement.addEventListener('mouseleave', resetMousePosition)
}

/** Remove all event listeners */
const removeEventListeners = () => {
	renderer.domElement.removeEventListener('mousedown', onPointerDown)
	renderer.domElement.removeEventListener('mousemove', onPointerMove)
	renderer.domElement.removeEventListener('mouseup', onPointerUp)
	renderer.domElement.removeEventListener('mouseleave', onPointerUp)

	renderer.domElement.removeEventListener('touchstart', onTouchStart)
	renderer.domElement.removeEventListener('touchmove', onTouchMove)
	renderer.domElement.removeEventListener('touchend', onPointerUp)

	window.removeEventListener('resize', onWindowResize)

	renderer.domElement.removeEventListener('mousemove', updateMousePosition)
	renderer.domElement.removeEventListener('mouseleave', resetMousePosition)
}

// ============================================================================
// VIDEO FRAME UPDATES
// ============================================================================
/**
 * Extract image index from currently hovered cell
 * @returns {number} Image index or -1 if not hovering
 */
const getHoveredImageIndex = () => {
	if (hoveredCell.x === NO_HOVER_VALUE || currentHoverIntensity <= 0.1) return -1

	const cellIdX = hoveredCell.x
	const foundCellIndex = mod(cellIdX, 100)
	const col = Math.floor(mod(cellIdX, NUM_COLUMNS * 100) / 100)

	const colData = columnData[col]
	if (!colData || foundCellIndex < 0 || foundCellIndex >= colData.cells.length) return -1

	return colData.cells[foundCellIndex].imageIndex
}

/**
 * Update video playback states and atlas frames
 * Only plays videos when their cell is hovered
 */
const updateVideoFramesInAtlas = () => {
	const hoveredIndex = getHoveredImageIndex()
	const uniforms = plane?.material.uniforms
	if (!uniforms) return

	videoElements.forEach((video, index) => {
		if (!video || !isVideoTexture[index]) return
		const shouldPlay = index === hoveredIndex
		if (shouldPlay && video.paused) video.play().catch(() => {})
		else if (!shouldPlay && !video.paused) {
			video.pause()
			video.currentTime = 0
		}

		if (!atlasVideoPatched[index] && video.readyState >= 2) {
			patchVideoTileInAtlas(index)
		}
	})

	if (hoveredIndex !== -1 && isVideoTexture[hoveredIndex] && videoElements[hoveredIndex]) {
		if (uniforms.uActiveVideoIndex.value !== hoveredIndex) {
			uniforms.uActiveVideoTexture.value = videoTextures[hoveredIndex]
			uniforms.uActiveVideoIndex.value = hoveredIndex
		}
	} else {
		uniforms.uActiveVideoTexture.value = null
		uniforms.uActiveVideoIndex.value = -1.0
	}
}

// ============================================================================
// HOVER DETECTION
// ============================================================================
/**
 * Convert mouse position to world coordinates
 * @param {number} mouseX - Mouse X position in pixels
 * @param {number} mouseY - Mouse Y position in pixels
 * @param {number} width - Canvas width
 * @param {number} height - Canvas height
 * @returns {{x: number, y: number}} World coordinates
 */
const mouseToWorldCoords = (mouseX, mouseY, width, height) => {
	// Convert mouse to screen UV (-1 to 1)
	let screenUV = {
		x: (mouseX / width) * 2 - 1,
		y: -((mouseY / height) * 2 - 1),
	}

	// Apply fisheye distortion correction
	const radius = Math.sqrt(screenUV.x * screenUV.x + screenUV.y * screenUV.y)
	const distortion = 1 - currentDistortion * radius * radius
	screenUV.x *= distortion
	screenUV.y *= distortion

	// Apply aspect ratio and zoom
	const aspectRatio = width / height
	return {
		x: screenUV.x * aspectRatio * zoomLevel + offset.x,
		y: screenUV.y * zoomLevel + offset.y,
	}
}

/**
 * Find cell at given world coordinates
 * @param {number} worldX - World X coordinate
 * @param {number} worldY - World Y coordinate
 * @returns {{cellX: number, cellY: number} | null} Cell ID or null if not found
 */
const findCellAtPosition = (worldX, worldY) => {
	const totalWidth = NUM_COLUMNS * COLUMN_WIDTH
	const col = Math.floor(mod(worldX, totalWidth) / COLUMN_WIDTH)
	const colPatternX = Math.floor(worldX / totalWidth)

	const colData = columnData[col]
	if (!colData || colData.totalHeight <= 0) return null

	// Apply column Y offset and find cell
	const colY = worldY - (COLUMN_OFFSETS[col] || 0)
	const colPatternY = Math.floor(colY / colData.totalHeight)
	const localY = mod(colY, colData.totalHeight)

	// Find which cell in this column
	let foundCellIndex = -1
	for (let i = 0; i < colData.cells.length; i++) {
		const cell = colData.cells[i]
		if (localY >= cell.y && localY < cell.y + cell.height) {
			foundCellIndex = i
			break
		}
	}

	if (foundCellIndex === -1) return null

	return {
		cellX: colPatternX * NUM_COLUMNS * 100 + col * 100 + foundCellIndex,
		cellY: colPatternY,
	}
}

/**
 * Update the currently hovered cell based on mouse position
 */
const updateHoveredCell = () => {
	if (mousePosition.x >= 0 && plane?.material.uniforms) {
		const container = canvasRef.value
		if (container) {
			const { offsetWidth: width, offsetHeight: height } = container
			const worldCoord = mouseToWorldCoords(mousePosition.x, mousePosition.y, width, height)
			const cell = findCellAtPosition(worldCoord.x, worldCoord.y)

			if (cell && (cell.cellX !== hoveredCell.x || cell.cellY !== hoveredCell.y)) {
				// Cell changed - store previous and update current
				prevHoveredCell.set(hoveredCell.x, hoveredCell.y)
				prevHoverIntensity = currentHoverIntensity
				hoveredCell.set(cell.cellX, cell.cellY)
				currentHoverIntensity = 0
				targetHoverIntensity = 1
			}
		}
	} else {
		// Mouse left canvas - fade out current cell
		if (targetHoverIntensity !== 0) {
			prevHoveredCell.set(hoveredCell.x, hoveredCell.y)
			prevHoverIntensity = currentHoverIntensity
			hoveredCell.set(NO_HOVER_VALUE, NO_HOVER_VALUE)
			currentHoverIntensity = 0
			targetHoverIntensity = 0
		}
	}

	isTouchActive = false
}

// ============================================================================
// ANIMATION LOOP
// ============================================================================
/**
 * Main animation loop - handles interpolation, hover detection, and rendering
 */
const animate = () => {
	if (isDestroyed) return
	animationFrameId = requestAnimationFrame(animate)

	// Smooth offset interpolation
	offset.x = lerp(offset.x, targetOffset.x, config.lerpFactor)
	offset.y = lerp(offset.y, targetOffset.y, config.lerpFactor)

	// Distortion and zoom tied to drag state
	const maxDistortion = 0.08
	targetDistortion = isDragging ? maxDistortion : 0
	currentDistortion = lerp(currentDistortion, targetDistortion, config.lerpFactor)

	// Zoom follows distortion progress
	const distortionProgress = currentDistortion / maxDistortion
	zoomLevel = 1 + (config.zoomLevel - 1) * distortionProgress

	// Radial blur follows distortion
	const targetRadialBlurIntensity = distortionProgress * config.radialBlurIntensity
	const currentRadialBlurIntensity = plane?.material.uniforms.uRadialBlurIntensity.value || 0
	const newRadialBlurIntensity = lerp(currentRadialBlurIntensity, targetRadialBlurIntensity, config.lerpFactor)

	// Update hover detection
	if (!isTouchActive) updateHoveredCell()
	// Smooth hover transitions (0.3s at 60fps ≈ 0.1 lerp factor)
	const hoverLerpSpeed = 0.1
	currentHoverIntensity = lerp(currentHoverIntensity, targetHoverIntensity, hoverLerpSpeed)
	prevHoverIntensity = lerp(prevHoverIntensity, 0, hoverLerpSpeed)

	// Clear previous cell when fully faded
	if (prevHoverIntensity < 0.01) {
		prevHoverIntensity = 0
		prevHoveredCell.set(NO_HOVER_VALUE, NO_HOVER_VALUE)
	}

	// Update shader uniforms
	updateShaderUniforms(newRadialBlurIntensity)

	// Update video frames in atlas
	const hasActiveVideos = videoElements.some((v) => v && !v.paused)
	const hasHoveredVideo = getHoveredImageIndex() !== -1

	if (hasActiveVideos || hasHoveredVideo) updateVideoFramesInAtlas()

	renderer?.render(scene, camera)
}

/**
 * Update all shader uniforms with current values
 * @param {number} radialBlurIntensity - Current radial blur intensity
 */
const updateShaderUniforms = (radialBlurIntensity) => {
	if (!plane?.material.uniforms) return

	const { uniforms } = plane.material
	uniforms.uOffset.value.set(offset.x, offset.y)
	uniforms.uZoom.value = zoomLevel
	uniforms.uDistortion.value = currentDistortion
	uniforms.uHoverIntensity.value = currentHoverIntensity
	uniforms.uPrevHoverIntensity.value = prevHoverIntensity
	uniforms.uRadialBlurIntensity.value = radialBlurIntensity
	uniforms.uHoveredCell.value.set(hoveredCell.x, hoveredCell.y)
	uniforms.uPrevHoveredCell.value.set(prevHoveredCell.x, prevHoveredCell.y)

	if (!introComplete) {
		const elapsed = performance.now() - introStartTime - INTRO_DELAY
		if (elapsed <= 0) return

		const t = Math.min(elapsed / INTRO_DURATION, 1.0)
		introProgress = easeOutQuint(t)
		uniforms.uIntroProgress.value = introProgress

		if (t >= 1.0) {
			uniforms.uIntroProgress.value = 1.0
			introComplete = true
		}
	}
}

let animationFrameId = null

// ============================================================================
// INITIALIZATION & CLEANUP
// ============================================================================
/**
 * Initialize the WebGL scene, textures, and start animation
 * @returns {Promise<void>}
 */
const init = async () => {
	const container = canvasRef.value
	if (!container) return

	isDestroyed = false
	textureLoader = new THREE.TextureLoader()

	// Setup Three.js scene
	scene = new THREE.Scene()
	camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 10)
	camera.position.z = 1

	// Setup renderer
	renderer = new THREE.WebGLRenderer({ antialias: false, alpha: false, powerPreference: 'high-performance', depth: false })
	renderer.setSize(container.offsetWidth, container.offsetHeight)
	renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5))

	const bgColor = rgbaToArray(config.backgroundColor)
	renderer.setClearColor(new THREE.Color(bgColor[0], bgColor[1], bgColor[2]), bgColor[3])
	container.appendChild(renderer.domElement)

	// Load textures and create atlases
	const loadedImageTextures = await loadTexture()
	imageAtlas = createTextureAtlas(loadedImageTextures, false)
	textAtlas = createTextureAtlas(textTextures, true)

	// Generate column layout and create texture
	updateGridDimensions(container.offsetWidth, container.offsetHeight)

	// NUM_COLUMNS = getDynamicColumns()
	generateColumnLayout()
	layoutTexture = createLayoutTexture()

	// Create shader uniforms
	const uniforms = createShaderUniforms(container)

	// Create plane with shader material
	const geometry = new THREE.PlaneGeometry(2, 2)
	const material = new THREE.ShaderMaterial({
		vertexShader: INFINITE_GALLERY_VERTEX_SHADER,
		fragmentShader: INFINITE_GALLERY_FRAGMENT_SHADER,
		uniforms,
	})

	plane = new THREE.Mesh(geometry, material)
	scene.add(plane)

	addEventListeners()
	introStartTime = performance.now()
	animationFrameId = requestAnimationFrame(animate)
}

/**
 * Cleanup all resources and dispose Three.js objects
 */
const destroy = () => {
	isDestroyed = true
	introProgress = 0
	introComplete = false
	introStartTime = null
	removeEventListeners()

	if (animationFrameId) {
		cancelAnimationFrame(animationFrameId)
		animationFrameId = null
	}

	imageTextures.forEach((texture) => texture?.dispose())
	imageTextures.length = 0

	textTextures.forEach((texture) => texture?.dispose())
	textTextures.length = 0

	videoTextures.forEach((texture) => texture?.dispose())
	videoTextures.length = 0

	videoElements.forEach((video) => {
		if (video) {
			video.pause()
			video.src = ''
			if (video.parentNode) video.parentNode.removeChild(video)
		}
	})
	videoElements.length = 0
	isVideoTexture.length = 0
	atlasVideoPatched.length = 0

	textCanvases.length = 0
	imageAspectRatios.length = 0
	columnData.length = 0

	if (imageAtlas) {
		imageAtlas.dispose()
		imageAtlas = null
	}
	if (textAtlas) {
		textAtlas.dispose()
		textAtlas = null
	}
	if (layoutTexture) {
		layoutTexture.dispose()
		layoutTexture = null
	}

	if (plane) {
		if (plane.material.uniforms.uActiveVideoTexture?.value) {
			plane.material.uniforms.uActiveVideoTexture.value = null
		}
		scene.remove(plane)
		plane.geometry.dispose()
		plane.material.dispose()
		plane = null
	}

	if (renderer) {
		renderer.dispose()
		renderer.domElement.remove()
		renderer = null
	}

	textureLoader = null
	scene = null
	camera = null
}

onMounted(() => {
	init()
})

onBeforeUnmount(() => {
	destroy()
})
</script>

<template>
	<section class="h-dvh w-full">
		<div ref="canvasRef" class="h-full w-full cursor-grab" />

		<div class="pointer-events-none absolute bottom-0 left-0 z-10 flex w-full grid-cols-3 flex-col gap-3 bg-gradient-to-t from-black/90 p-5 md:grid md:min-h-56 md:items-end md:gap-10 md:p-10">
			<div class="flex w-fit flex-col items-start gap-5">
				<img src="https://cdn.papercrowns.com/website/images/elements/etches/portal.png" alt="Lost Art" class="h-32" />
				<p class="pointer-events-auto text-sm text-crowns-off-white/75 md:text-base">Curated from the castle cellars.</p>
			</div>
			<div class="w-fit md:mx-auto">
				<div class="pointer-events-auto flex items-center gap-x-1.5 rounded-full border border-white bg-black px-2 text-sm md:border-2 md:text-base">
					<p class="text-white">Drag to see more</p>
					<div class="md:order-l-2 flex aspect-square w-5 items-center justify-center border-l border-white py-0.5 pl-1.5">
						<i class="fa-regular fa-arrows-up-down-left-right" />
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

<style lang="css">
.dragging {
	cursor: grabbing !important;
}
</style>
